import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

type AuthorizationStatus = 'Enable' | 'Disable';
type AuthorizationItem = {
  id: string;
  countryCode: string;
  mobile: string;
  remark?: string;
  status: AuthorizationStatus;
  createTime?: number;
};

@Component({
  selector: 'liteconsole-login-authorization',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzFormModule,
    NzInputModule,
    NzModalModule,
    NzPaginationModule,
    NzSwitchModule,
    NzTableModule,
    NzTagModule,
    NzIconModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './liteconsole-login-authorization.component.html',
  styleUrls: ['./liteconsole-login-authorization.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LiteConsoleLoginAuthorizationComponent implements OnInit {
  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  form = this.fb.group({ mobiles: ['', Validators.required], remark: [''] });
  items: AuthorizationItem[] = [];
  loading = false;
  saving = false;
  key = '';
  pageIndex = 1;
  pageSize = 20;
  total = 0;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.http
      .post(ConstantUtil.ADMIN_LITECONSOLE_LOGIN_AUTH_LIST, {
        pageIndex: this.pageIndex - 1,
        pageSize: this.pageSize,
        key: this.key
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success) {
          this.items = res.data?.items ?? [];
          this.total = Number(res.data?.count ?? res.data?.totalCount ?? this.items.length);
        } else this.message.error(res?.msg ?? this.i18n.fanyi('liteconsole.login-auth.load-failed'));
      });
  }

  save(): void {
    const items = this.parseMobiles(this.form.value.mobiles ?? '');
    if (!items.length) {
      this.message.warning(this.i18n.fanyi('liteconsole.login-auth.invalid-mobile'));
      return;
    }
    this.saving = true;
    const remark = String(this.form.value.remark ?? '').trim();
    this.http
      .post(ConstantUtil.ADMIN_LITECONSOLE_LOGIN_AUTH_SAVE, {
        items: items.map(item => ({ ...item, remark }))
      })
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (!res?.success) {
          this.message.error(res?.msg ?? this.i18n.fanyi('liteconsole.login-auth.save-failed'));
          return;
        }
        this.message.success(this.i18n.fanyi('liteconsole.login-auth.save-success'));
        this.form.reset({ mobiles: '', remark: '' });
        this.pageIndex = 1;
        this.load();
      });
  }

  setStatus(item: AuthorizationItem, enabled: boolean): void {
    this.http
      .post(ConstantUtil.ADMIN_LITECONSOLE_LOGIN_AUTH_STATUS_SET, {
        id: item.id,
        status: enabled ? 'Enable' : 'Disable'
      })
      .subscribe(res => {
        if (res?.success) {
          item.status = enabled ? 'Enable' : 'Disable';
          this.message.success(this.i18n.fanyi('liteconsole.login-auth.status-success'));
        } else {
          this.message.error(res?.msg ?? this.i18n.fanyi('liteconsole.login-auth.status-failed'));
          this.load();
        }
        this.cdr.detectChanges();
      });
  }

  confirmDelete(item: AuthorizationItem): void {
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('liteconsole.login-auth.delete-confirm-title'),
      nzContent: `${this.i18n.fanyi('liteconsole.login-auth.delete-confirm-content')} ${item.countryCode} ${item.mobile}`,
      nzOkDanger: true,
      nzOnOk: () =>
        this.http.post(ConstantUtil.ADMIN_LITECONSOLE_LOGIN_AUTH_DELETE, { ids: [item.id] }).subscribe(res => {
          if (res?.success) {
            this.message.success(this.i18n.fanyi('liteconsole.login-auth.delete-success'));
            this.load();
          } else this.message.error(res?.msg ?? this.i18n.fanyi('liteconsole.login-auth.delete-failed'));
        })
    });
  }

  search(value: string): void {
    this.key = value.trim();
    this.pageIndex = 1;
    this.load();
  }
  pageChange(value: number): void {
    this.pageIndex = value;
    this.load();
  }

  private parseMobiles(raw: string): Array<{ countryCode: string; mobile: string }> {
    const seen = new Set<string>();
    return raw
      .split(/[\n,，;；]+/)
      .map(value => value.replace(/[\s-]/g, ''))
      .filter(Boolean)
      .map(value =>
        value.startsWith('+86')
          ? { countryCode: '+86', mobile: value.slice(3) }
          : { countryCode: '+886', mobile: value.startsWith('+886') ? value.slice(4).replace(/^0/, '') : value.replace(/^0/, '') }
      )
      .filter(item => (item.countryCode === '+86' ? /^1[3-9]\d{9}$/.test(item.mobile) : /^9\d{8}$/.test(item.mobile)))
      .filter(item => {
        const id = `${item.countryCode}:${item.mobile}`;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
  }
}
